import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react'

import { FIELD } from '@/components/ui/formFields'
import { Icon } from '@/components/ui/Icon'
import { cn } from '@/lib/cn'
import { ChevronDown, X } from '@/lib/icons'

/*
 * ARIA 1.2 combobox (APG "Combobox with List Autocomplete"): a `role="combobox"`
 * input owning a `role="listbox"` popup of `role="option"`s, keyboard driven
 * from the input via `aria-activedescendant`. jsx-a11y's `prefer-tag-over-role`
 * assumes `<select>`, which can't filter — those rules are disabled for this
 * file in `.oxlintrc.json`.
 */
export interface ComboboxOption {
  value: string
  label: string
}

interface ComboboxProps {
  id: string
  /** Committed value; `''` means nothing selected. */
  value: string
  onChange: (next: string) => void
  options: readonly ComboboxOption[]
  /** Filter + order options for a query. Default: prefix then substring, case-insensitive. */
  filter?: (
    query: string,
    options: readonly ComboboxOption[],
  ) => ComboboxOption[]
  placeholder?: string
  disabled?: boolean
  /** Accessible name for the popup listbox. */
  listLabel: string
  /** Accessible name for the clear button. */
  clearLabel: string
  /** Shown below the field when a query matches nothing. */
  emptyMessage?: string
}

function defaultFilter(
  query: string,
  options: readonly ComboboxOption[],
): ComboboxOption[] {
  const q = query.trim().toLowerCase()
  if (!q) {
    return [...options]
  }
  const prefix: ComboboxOption[] = []
  const substring: ComboboxOption[] = []
  for (const option of options) {
    const label = option.label.toLowerCase()
    if (label.startsWith(q)) {
      prefix.push(option)
    } else if (label.includes(q)) {
      substring.push(option)
    }
  }
  return [...prefix, ...substring]
}

export function Combobox({
  id,
  value,
  onChange,
  options,
  filter = defaultFilter,
  placeholder,
  disabled,
  listLabel,
  clearLabel,
  emptyMessage,
}: ComboboxProps) {
  const listboxId = useId()
  const rootRef = useRef<HTMLDivElement>(null)

  // `query === null` means "not editing" — the input shows the canonical label
  // for `value`, so an externally-changed `value` flows through with no effect
  // and no state sync.
  const [query, setQuery] = useState<string | null>(null)
  const [open, setOpen] = useState(false)
  const [activeIndex, setActiveIndex] = useState(-1)

  const labelFor = useCallback(
    (code: string) => options.find((o) => o.value === code)?.label ?? '',
    [options],
  )

  const text = query ?? labelFor(value)
  const matches =
    query === null || query === labelFor(value)
      ? [...options]
      : filter(query, options)
  const noMatch = query !== null && query.trim() !== '' && matches.length === 0
  // A `role="listbox"` may only hold `role="option"`, so an empty result closes
  // the popup and the message renders as helper text instead.
  const listOpen = open && !noMatch

  function reset() {
    setOpen(false)
    setActiveIndex(-1)
    setQuery(null)
  }

  function commit(option: ComboboxOption) {
    onChange(option.value)
    reset()
  }

  function seedIndex(list: readonly ComboboxOption[]): number {
    if (!list.length) {
      return -1
    }
    if (!value) {
      return 0
    }
    const found = list.findIndex((o) => o.value === value)
    return found >= 0 ? found : 0
  }

  // Keep the active option in view (no setState → no `set-state-in-effect`).
  useEffect(() => {
    if (open && activeIndex >= 0) {
      document
        .getElementById(`${listboxId}-opt-${activeIndex}`)
        ?.scrollIntoView({ block: 'nearest' })
    }
  }, [open, activeIndex, listboxId])

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (disabled) {
      return
    }
    switch (event.key) {
      case 'ArrowDown': {
        event.preventDefault()
        if (!open) {
          setOpen(true)
          setActiveIndex(seedIndex(matches))
        } else {
          setActiveIndex((i) => Math.min(i + 1, matches.length - 1))
        }
        return
      }
      case 'ArrowUp': {
        event.preventDefault()
        if (!open) {
          setOpen(true)
          setActiveIndex(matches.length ? matches.length - 1 : -1)
        } else {
          setActiveIndex((i) => Math.max(i - 1, 0))
        }
        return
      }
      case 'Home': {
        if (open) {
          event.preventDefault()
          setActiveIndex(0)
        }
        return
      }
      case 'End': {
        if (open) {
          event.preventDefault()
          setActiveIndex(matches.length - 1)
        }
        return
      }
      case 'Enter': {
        if (open && activeIndex >= 0) {
          const picked = matches[activeIndex]
          if (picked) {
            event.preventDefault()
            commit(picked)
          }
        }
        return
      }
      case 'Escape': {
        if (open) {
          event.stopPropagation()
          reset()
        } else if (value) {
          onChange('')
          setQuery(null)
        }
        return
      }
      case 'Tab': {
        if (open) {
          const picked = activeIndex >= 0 ? matches[activeIndex] : undefined
          if (picked) {
            commit(picked)
          } else {
            reset()
          }
        }
        return
      }
      default:
        return
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={listOpen}
        aria-controls={listboxId}
        aria-autocomplete="list"
        aria-activedescendant={
          listOpen && activeIndex >= 0
            ? `${listboxId}-opt-${activeIndex}`
            : undefined
        }
        autoComplete="off"
        spellCheck={false}
        value={text}
        placeholder={placeholder}
        disabled={disabled}
        className={cn(FIELD, 'pr-20')}
        onChange={(event) => {
          const next = event.target.value
          setQuery(next)
          setOpen(true)
          setActiveIndex(filter(next, options).length ? 0 : -1)
        }}
        onFocus={(event) => {
          event.currentTarget.select()
        }}
        onClick={() => {
          if (!open && !disabled) {
            setOpen(true)
            setActiveIndex(seedIndex(options))
          }
        }}
        onKeyDown={handleKeyDown}
        onBlur={(event) => {
          if (!rootRef.current?.contains(event.relatedTarget)) {
            reset()
          }
        }}
      />

      <Icon
        icon={ChevronDown}
        className="text-fg-muted pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2"
      />

      {value !== '' && !disabled && (
        <button
          type="button"
          aria-label={clearLabel}
          onClick={() => {
            onChange('')
            reset()
          }}
          className="text-fg-muted hover:text-fg absolute top-1/2 right-9 flex min-h-11 min-w-11 -translate-y-1/2 items-center justify-center rounded-md"
        >
          <Icon icon={X} className="size-4" />
        </button>
      )}

      <ul
        id={listboxId}
        role="listbox"
        aria-label={listLabel}
        hidden={!listOpen}
        onMouseDown={(event) => {
          event.preventDefault()
        }}
        className="border-glass-border bg-surface-container-low backdrop-blur-glass absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-md border shadow-xl"
      >
        {listOpen &&
          matches.map((option, index) => (
            <li
              key={option.value}
              id={`${listboxId}-opt-${index}`}
              role="option"
              aria-selected={option.value === value}
              onClick={() => {
                commit(option)
              }}
              onMouseEnter={() => {
                setActiveIndex(index)
              }}
              className={cn(
                'font-body text-body-md cursor-pointer px-4 py-3 text-pretty',
                index === activeIndex
                  ? 'bg-primary-container/15 text-primary'
                  : 'text-fg',
              )}
            >
              {option.label}
            </li>
          ))}
      </ul>

      {noMatch && emptyMessage && (
        <p className="font-body text-label-sm text-fg-muted mt-1">
          {emptyMessage}
        </p>
      )}
      <span className="sr-only" aria-live="polite">
        {listOpen
          ? `${matches.length} ${matches.length === 1 ? 'result' : 'results'}`
          : noMatch
            ? (emptyMessage ?? 'No results')
            : ''}
      </span>
    </div>
  )
}
